import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Typography } from "@material-ui/core";
import moment from "moment";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import backgroundProvider from "../../../../../assets/img/backgroundProvider.png";
import DatePicker from "../../../../../components/common/DatePicker";
import ShowImage from "../../../../../components/common/ShowImage";
import UserContext from "../../../../../context/user-context";
import useInputUserChange from "../../../../../hooks/useInputUserChange";
import useShowImage from "../../../../../hooks/useShowImage";
import useStateDate from "../../../../../hooks/useStateDate";
import { useTranslation } from "../../../../../hooks/useTranslation";
import useUploadImage from "../../../../../hooks/useUploadImage";
import { setUserChange } from "../../../../../store/actions/userAction";
import { fileNames } from "../../../../../utils/imageUtil";
import { EntrantTabIndex } from "../../../../../utils/tabIndex";
import { driverLicenseNewVehicleSchema } from "../../../../../utils/validations/DriverLicenseNewVehicleSchema";

type DriverLicenseSectionProps = {
  children?: React.ReactNode;
  modified?: () => void;
};

const DriverLicenseSection = ({ children, modified }: DriverLicenseSectionProps) => {
  const getImage = useShowImage();
  const { state, dispatch } = useContext(UserContext);
  const { t, BundleKey } = useTranslation();
  const { uploadNewPhoto } = useUploadImage();
  const [frontDriverLicense, setFrontDriverLicense] = useState<any>(
    state.frontDriverLicensePhoto || getImage(state.frontDriverLicense)
  );
  const [backDriverLicense, setBackDriverLicense] = useState<any>(
    state.backDriverLicensePhoto || getImage(state.backDriverLicense)
  );
  const { handleDatePickerChange } = useInputUserChange();

  const { register } = useForm({
    resolver: yupResolver(driverLicenseNewVehicleSchema(t)),
  });
  const { date, setDate } = useStateDate(state.driverLicenseExpirationDate);

  const uploadNewFrontLicensePhoto = (newPhoto: any) => {
    setFrontDriverLicense(newPhoto);
    dispatch(setUserChange("frontDriverLicensePhoto", newPhoto));
    uploadNewPhoto(
      newPhoto,
      "frontDriverLicense",
      fileNames.FRONT_LICENSE_FILENAME,
      state.registrationType !== "full"
    );
  };

  const uploadNewBackLicensePhoto = (newPhoto: any) => {
    setBackDriverLicense(newPhoto);
    dispatch(setUserChange("backDriverLicensePhoto", newPhoto));
    uploadNewPhoto(
      newPhoto,
      "backDriverLicense",
      fileNames.BACK_LICENSE_FILENAME,
      state.registrationType !== "full"
    );
  };

  return (
    <Grid container spacing={3} style={{ paddingInline: 8 }}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {t(BundleKey.DETAIL_ENTRYMETHOD_CAR_LICENSE_TITLE)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DatePicker
          name="driverLicenseExpirationDate"
          id="driverLicenseExpirationDate"
          label={t(BundleKey.DETAIL_ENTRYMETHOD_CAR_LICENSE_DATE)}
          size="medium"
          tabIndex={EntrantTabIndex.dateLicense}
          value={date}
          minDate={moment().add(1, "day").toDate()}
          minDateMessage={t(BundleKey.DETAIL_ENTRYMETHOD_CAR_LICENSE_DATE_ERROR)}
          onchange={(date) => {
            setDate(date);
            handleDatePickerChange(
              "driverLicenseExpirationDate",
              date,
              "isInvalidDriverLicenseExpirationDate",
              () => {
                if (modified) modified();
              }
            );
          }}
          registration={register("driverLicenseExpirationDate")}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ShowImage
          title={t(BundleKey.DETAIL_ENTRYMETHOD_CAR_LICENSE_FRONTPHOTO)}
          defaultImage={backgroundProvider}
          image={frontDriverLicense !== "" ? frontDriverLicense : state.frontDriverLicense}
          uploadNewPhoto={uploadNewFrontLicensePhoto}
          tabIndex={EntrantTabIndex.hidden}
          alt="frontDriverLicense"
          circle={false}
          small
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ShowImage
          title={t(BundleKey.DETAIL_ENTRYMETHOD_CAR_LICENSE_BACKPHOTO)}
          defaultImage={backgroundProvider}
          image={backDriverLicense !== "" ? backDriverLicense : state.backDriverLicense}
          tabIndex={EntrantTabIndex.hidden}
          uploadNewPhoto={uploadNewBackLicensePhoto}
          alt="backDriverLicense"
          circle={false}
          small
        />
      </Grid>
      <Grid item xs={12}>
        {children}
      </Grid>
    </Grid>
  );
};

export default DriverLicenseSection;
